import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import ApiServiceDGB from './shared/ApiServiceDGB';
import { Toaster } from 'react-hot-toast';
import { notifySuccess } from './ToastMessages';
import formatPhoneUS from './helpers/formatPhoneUS';
import moment from "moment-timezone";
import TimeframeRangeLabel from './TimeframeRangeLabel';
import { timeframeRangeText } from './helpers/timeframeRange';

// Sorting Icon
const SortIcon = () => {
    return (
        <div className="table-header-sort-icons">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M201.4 137.4c12.5-12.5 32.8-12.5 45.3 0l160 160c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L224 205.3 86.6 342.6c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3l160-160z"/></svg>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M201.4 374.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 306.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z"/></svg>
        </div>
    );
};

// Table Row
const Row = ({ item, deleteVin }) => {
    const attachmentsNames = item.attachments
        .map(attachment => `${attachment.originalName}.${attachment.extension}`)
        .join(',\n');

    const approvedPhoneNumbers = item.users.reduce((acc, user) => {
        return acc.concat(
            user.phones
                .filter(phone => phone.approvedAt)
                .map(phone => formatPhoneUS(phone.phoneNumber))
        );
    }, []).join(', ');

    const formattedDate = new Date(item.createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
    });

    const triggerDeleteVin = () => {
        deleteVin(item["_id"]);
    }

    return (
        <div className="row ml-0 mr-0 no-highlight">
            <div className="col-2"><a href={`manage-vins/${item.vin}`}>{item.vin}</a></div>
            <div className="col-1">{item?.year ?? "-"}</div>
            <div className="col-1">{item?.make ?? "-"}</div>
            <div className="col-1">{item?.model?? "-"}</div>
            <div className="col-1">{formattedDate}</div>
            <div className="col-1">
                {item?.addedBy?.firstName?.length > 6 && item?.addedBy?.lastName
                    ? item?.addedBy.firstName + ' ' + item?.addedBy.lastName
                    : "N/A"
                }
            </div>

            <div className="col-1">
                {item.users[0]?.firstName && item.users[0]?.lastName
                    ? `${item?.users[0].firstName} ${item?.users[0].lastName}`
                    : "N/A"
                }
            </div>

            <div className="col-1">
                {approvedPhoneNumbers.length > 0
                    ? <i className="fas fa-phone"
                         data-toggle="tooltip"
                         data-title={approvedPhoneNumbers}
                    ></i>
                    : __("Unassigned")
                }
            </div>

            <div className="col-1 text-center">
                {item?.attachments.length > 0
                    ? <i className="fas fa-paperclip"
                         data-search="attach attachment connect link paperclip"
                         data-toggle="tooltip"
                         data-title={attachmentsNames}
                    ></i>
                    : __("None")
                }
            </div>
            <div className="col-1">
                <button className="btn" onClick={() => triggerDeleteVin(item["_id"])}>
                    <i className='fas fa-trash'></i>
                </button>
            </div>
        </div>
    );
};

// Fetch Cars
async function fetchCars(jwt, url) {
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${jwt}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            const errorText = await response.text();
            if (errorText.toLowerCase().includes('invalid token')) {
                window.location.replace(window.location.origin + '/logout');
            }
            throw new Error(errorText || 'Network response was not ok');
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching cars:', error);
        throw error;
    }
}

class ManageVins extends React.Component {
    constructor(props) {
        super(props)
        this.state = {
            jwt: this.props.state.jwt,
            dgbApiUrl: this.props.state.dgbApiUrl,
            businessRef: this.props.business.reference,
            cars: [],
            loading: true,
            sortOrder: { addedBy: 'desc', createdAt: 'asc' },
            limit: 25,
            paginationFirstItemIndex: 1,
            paginationLastItemIndex: this.cars?.limit || 10,
            searchQuery: '',
            timeframe: 'last30',
            timeframeFilter: null,
            customTimeStart: null,
            customTimeEnd: null,
            businessTimezone: this.props.business.call_at_timezone,
        };

        this.apiService = new ApiServiceDGB(this.state.dgbApiUrl, this.props.state.jwt);

        this.fetchAllCars = this.fetchAllCars.bind(this);
        this.toggleSortOrder = this.toggleSortOrder.bind(this);
        this.loadNextPage = this.loadNextPage.bind(this);
        this.loadPrevPage = this.loadPrevPage.bind(this);
        this.handleLimitChange = this.handleLimitChange.bind(this);
        this.handleVinSearch = this.handleVinSearch.bind(this);
        this.deleteVin = this.deleteVin.bind(this);
        this.handleTimeframeClick = this.handleTimeframeClick.bind(this);
        this.handleCustomDate = this.handleCustomDate.bind(this);
    }

    async fetchAllCars(withSort, sortKey, page = 1, limit = this.state.limit, searchQuery = this.state.searchQuery, timeframeFilter = this.state.timeframeFilter) {
        const { jwt, dgbApiUrl, sortOrder, businessRef } = this.state;

        let tempApiUrlGetCars = `${dgbApiUrl}/business/${businessRef}/cars?page=${page}&limit=${limit}&search[vin]=${searchQuery}`;

        if (timeframeFilter) {
            tempApiUrlGetCars += timeframeFilter;
        }

        if (withSort) {
            tempApiUrlGetCars += `&sort=${sortKey}:${sortOrder[sortKey]}`;
        }

        try {
            const cars = await fetchCars(jwt, tempApiUrlGetCars);
            const paginationFirstItemIndex = (page - 1) * cars.limit + 1;
            const paginationLastItemIndex = Math.min(page * cars.limit, cars.items.length);

            this.setState({ cars, loading: false, page, limit, paginationFirstItemIndex, paginationLastItemIndex });
        } catch (error) {
            this.setState({ loading: false });
        }
    }

    // sorting
    toggleSortOrder(field) {
        this.setState(prevState => ({
            sortOrder: {
                ...prevState.sortOrder,
                [field]: prevState.sortOrder[field] === 'asc' ? 'desc' : 'asc'
            }
        }), () => {
            this.fetchAllCars(true, field);
        });
    }

    // next page
    loadNextPage() {
        this.setState(prevState => ({
            page: this.state.cars.nextPage
        }), () => {
            this.fetchAllCars(false, Object.keys(this.state.sortOrder)[0], this.state.cars.nextPage);
        });
    }

    loadPrevPage() {
        this.setState(prevState => ({
            page: this.state.cars.prevPage
        }), () => {
            this.fetchAllCars(false, Object.keys(this.state.sortOrder)[0], this.state.cars.prevPage);
        });
    }

    // change limit
    handleLimitChange(event) {
        const newLimit = parseInt(event.target.value, 10);
        this.setState({ limit: newLimit, page: 1 }, () => {
            this.fetchAllCars(false, Object.keys(this.state.sortOrder)[0], 1, newLimit);
        });
    }

    //
    handleVinSearch(event) {
        const searchQuery = event.target.value.toLowerCase();
        this.setState({ searchQuery }, () => {
            this.fetchAllCars(false, Object.keys(this.state.sortOrder)[0], 1, this.state.limit, searchQuery);
        });
    }

    // Delete VIN
    deleteVin = async (carId) => {
        try {
            const response = await this.apiService.deleteVin(carId);
            if (response.status === 204) {
                this.setState(prevState => ({
                    cars: {
                        ...prevState.cars,
                        items: prevState.cars.items.filter(car => car["_id"] !== carId)
                    }
                }));

                notifySuccess(__('VIN removed successfully.'));
            }

        } catch (error) {
            console.error('Error fetching VIN details:', error);
        }
    }

    handleCustomDate(event, dateType) {
        const date = event.target.value;
        const localDate = moment.tz(date, this.state.businessTimezone);
        let dateTypeUTC = null;

        if (dateType === 'start') {
            dateTypeUTC = localDate.startOf("day").utc().format();
            this.setState({ customTimeStart: dateTypeUTC }, this.updateFilter);
        } else {
            dateTypeUTC = localDate.endOf("day").utc().format();
            this.setState({ customTimeEnd: dateTypeUTC }, this.updateFilter);
        }
    }

    updateFilter = () => {
        const { customTimeStart, customTimeEnd } = this.state;

        if (customTimeStart && customTimeEnd) {
            const filter = `&filter[gte][createdAt]=${customTimeStart}&filter[lte][createdAt]=${customTimeEnd}`;
            this.setState({ timeframeFilter: filter }, () => {
                this.fetchAllCars(false, Object.keys(this.state.sortOrder)[0], 1, this.state.limit, this.state.searchQuery, this.state.timeframeFilter);
            });
        }
    }

    handleTimeframeClick(e) {
        e.preventDefault();

        const id = e.target.id;

        // Get today's midnight and end of day in UTC - moment object
        const todayMidnightUTC = moment().tz(this.state.businessTimezone).startOf("day").utc();
        const todayEndDayUTC = moment().tz(this.state.businessTimezone).endOf("day").utc();

        let filter = "";

        // Calculate yesterday and beforeYesterday - also moment objects
        const yesterdayMidnight = todayMidnightUTC.clone().subtract(1, "days");
        const yesterdayEndDay = todayEndDayUTC.clone().subtract(1, "days");

        switch (id) {
            case "today":
                filter = `&filter[gte][createdAt]=${todayMidnightUTC.format()}&filter[lte][createdAt]=${todayEndDayUTC.format()}`;
                break;

            case "yesterday":
                filter = `&filter[gte][createdAt]=${yesterdayMidnight.format()}&filter[lte][createdAt]=${yesterdayEndDay.format()}`;
                break;

            case "weekToDate":
                const oneWeekAgo = todayMidnightUTC.clone().subtract(7, "days");
                filter = `&filter[gte][createdAt]=${oneWeekAgo.format()}&filter[lte][createdAt]=${todayEndDayUTC.format()}`;
                break;

            case "monthToDate":
                const oneMonthAgo = todayMidnightUTC.clone().startOf("month");
                filter = `&filter[gte][createdAt]=${oneMonthAgo.format()}&filter[lte][createdAt]=${todayEndDayUTC.format()}`;
                break;

            case "last30":
                const last30Days = todayMidnightUTC.clone().subtract(30, "days");
                filter = `&filter[gte][createdAt]=${last30Days.format()}&filter[lte][createdAt]=${todayEndDayUTC.format()}`;
                break;

            case "lifetime":
                filter = ""; // No date filter, fetches all data
                break;

            case "custom":
                filter = "";
                break;

            default:
                console.error("Invalid filter id");
        }

        // Use the filter in your API request
        this.setState({ timeframe: id, timeframeFilter: filter }, () => {
            this.fetchAllCars(false, Object.keys(this.state.sortOrder)[0], 1, this.state.limit, this.state.searchQuery, this.state.timeframeFilter);
        });
    }

    componentDidMount() {
        this.fetchAllCars();
        $('[data-toggle="tooltip"]').tooltip();
    }

    componentDidUpdate(prevProps, prevState) {
        // Reinitialize tooltips if the `cars` state changes
        if (prevState.cars !== this.state.cars) {
            $('[data-toggle="tooltip"]').tooltip();
        }
    }

    componentWillUnmount() {
        // Destroy tooltips when the component is unmounted
        $('[data-toggle="tooltip"]').tooltip('dispose');
    }


    render() {
        if (this.state.loading) {
            return <div>Loading...</div>;
        }

        /* If No QR for DGB is enabled */
        if (!this.props.state.hasQrCodeForDgb) {
            return (
                <div className="w-100 right-side-content">
                    <div className="main-section">
                        <div className="w-100 p-2 bg-white border-bottom shadow-sm d-flex align-items-center p-3">
                            <p className="mb-0 w-100">
                                {__('On this page you can view, add, or edit VINs registered to the Digital Glove Box')}.
                            </p>
                        </div>

                        <div className="w-100 p-2 bg-white shadow-sm d-flex flex-column align-items-center p-3">
                            <h5>{ __('Warning: No QR code has been set for Digital Glove Box.') }</h5>
                            <h5>{ __('Please do not add any VINs to the system until a QR code has been set!') }</h5>
                        </div>
                    </div>
                </div>
            );
        }

        const carItems = Array.isArray(this.state.cars.items) ? this.state.cars.items : [];
        const optRange = (id) => {
            const text = timeframeRangeText(id, this.state.businessTimezone, this.props.business?.created_at);
            return text ? <span className="timeframe-option-range ml-auto pl-3">{text}</span> : null;
        };

        return (
            <div className="w-100 right-side-content">
                <div className="main-section">
                    <div className="w-100 p-2 bg-white border-bottom shadow-sm d-flex align-items-center p-3">
                        <p className='mb-0 w-100'>{__('On this page you can view, add, or edit VINs registered to the Digital Glove Box')}.</p>

                        <a href="add-vin"
                           className="btn btn-primary text-nowrap ml-0 btn-add-vin-toggle">
                            <i className="fas fa-plus-circle mr-1"></i>
                            { __('Add VIN') }
                        </a>
                    </div>

                    <div className="w-100 p-2 bg-white border-bottom shadow-sm d-flex align-items-center p-3">
                        <div className="dropdown col-3">
                            <button className="dropdown-toggle second-input input-primary w-100 d-flex form-dropdown d-flex align-items-center roboto"
                                    type="button"
                                    id="dropdownMenuButton"
                                    data-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false">
                                <span>
                                    {this.state.timeframe === "monthToDate" ? __("Month to Date") : ""}
                                    {this.state.timeframe === "weekToDate" ? __("Week to Date") : ""}
                                    {this.state.timeframe === "today" ? __("Today") : ""}
                                    {this.state.timeframe === "yesterday" ? __("Yesterday") : ""}
                                    {this.state.timeframe === "lifetime" ? __("Lifetime") : ""}
                                    {this.state.timeframe === "last30" ? __("Last 30 Days") : ""}
                                    {this.state.timeframe === "custom" ? __("Custom Timeframe") : ""}
                                </span>
                            </button>
                            <div
                                className="dropdown-menu"
                                aria-labelledby="dropdownMenuButton"
                            >
                                <a
                                    className="dropdown-item d-flex align-items-center"
                                    onClick={(e) => this.handleTimeframeClick(e)}
                                    id="today"
                                    href="#"
                                >
                                    {__('Today')}{optRange('today')}
                                </a>
                                <a
                                    className="dropdown-item d-flex align-items-center"
                                    onClick={(e) => this.handleTimeframeClick(e)}
                                    id="yesterday"
                                    href="#"
                                >
                                    {__('Yesterday')}{optRange('yesterday')}
                                </a>
                                <a
                                    className="dropdown-item d-flex align-items-center"
                                    onClick={(e) => this.handleTimeframeClick(e)}
                                    id="weekToDate"
                                    href="#"
                                >
                                    {__('Week to Date')}{optRange('weekToDate')}
                                </a>
                                <a
                                    className="dropdown-item d-flex align-items-center"
                                    onClick={(e) => this.handleTimeframeClick(e)}
                                    id="monthToDate"
                                    href="#"
                                >
                                    {__('Month to Date')}{optRange('monthToDate')}
                                </a>
                                <a
                                    className="dropdown-item d-flex align-items-center"
                                    onClick={(e) => this.handleTimeframeClick(e)}
                                    id="last30"
                                    href="#"
                                >
                                    {__('Last 30 Days')}{optRange('last30')}
                                </a>
                                <a
                                    className="dropdown-item d-flex align-items-center"
                                    onClick={(e) => this.handleTimeframeClick(e)}
                                    id="lifetime"
                                    href="#"
                                >
                                    {__('Lifetime')}{optRange('lifetime')}
                                </a>
                                <a
                                    className="dropdown-item d-flex align-items-center"
                                    onClick={(e) => this.handleTimeframeClick(e)}
                                    id="custom"
                                    href="#"
                                >
                                    {__('Custom')}
                                </a>
                            </div>
                        </div>

                        {this.state.timeframe !== "custom" && (
                            <div className="col-9 d-flex flex-row align-items-center my-2">
                                <TimeframeRangeLabel
                                    timeframe={this.state.timeframe}
                                    timezone={this.state.businessTimezone}
                                    lifetimeStart={this.props.business?.created_at}
                                />
                            </div>
                        )}

                        {this.state.timeframe === "custom" && (
                            <div className="col-9 d-flex flex-row align-items-center dgb-custom-timeframe my-2">
                                <div className="card-title mb-0">
                                    <span className='mr-1'>{__("From")}:</span>
                                    <input
                                        className="input-primary second-input form-dropdown px-2 start-date"
                                        onChange={(e) => this.handleCustomDate(e, 'start')}
                                        type="date"
                                        name="custom_timeframe_start"
                                        id="custom_timeframe_start"
                                        required
                                    />
                                </div>

                                <div className="card-title ml-3 mb-0">
                                    <span className='mr-1'>{__("To")}:</span>
                                    <input
                                        className="input-primary second-input form-dropdown px-2 end-date"
                                        onChange={(e) => this.handleCustomDate(e, 'end')}
                                        type="date"
                                        name="custom_timeframe_end"
                                        id="custom_timeframe_end"
                                        required
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="w-100 p-2 bg-white shadow-sm d-flex align-items-center p-3 sortable-table">
                        {/* Table */}
                        <div className="col-xl-12 table">
                            {/* Table Header */}
                            <div className="table-header row ml-0 mr-0">
                                {/* Vin Column*/}
                                <div className="col-2">
                                    <div className="table-header-label-wrapper">
                                        <span>{__('VIN')}</span>
                                        <input
                                            type="text"
                                            placeholder="Search"
                                            onChange={this.handleVinSearch}
                                            className="form-control ml-0 dgb-search-input"
                                        />
                                    </div>
                                </div>

                                {/* Year */}
                                <div className="col-1">
                                    <div className="table-header-label-wrapper">
                                        <span>{__('Year')}</span>
                                    </div>
                                </div>

                                {/* Make */}
                                <div className="col-1">
                                    <div className="table-header-label-wrapper">
                                        <span>{__('Make')}</span>
                                    </div>
                                </div>

                                {/* Model */}
                                <div className="col-1">
                                    <div className="table-header-label-wrapper">
                                        <span>{__('Model')}</span>
                                    </div>
                                </div>

                                {/* Date Added */}
                                <div className="col-1">
                                    <div
                                        onClick={() => this.toggleSortOrder('createdAt')}
                                        className="table-header-label-wrapper"
                                        data-asc={this.state.sortOrder.createdAt === 'desc'}
                                    >
                                        <span>{__('Date Added')}</span>
                                        <SortIcon/>
                                    </div>
                                </div>

                                {/* Added By */}
                                <div className="col-1">
                                    <div
                                        onClick={() => this.toggleSortOrder('addedBy')}
                                        className="table-header-label-wrapper"
                                        data-asc={this.state.sortOrder.addedBy === 'desc'}
                                    >
                                        <span>{__('Added By')}</span>
                                        <SortIcon/>
                                    </div>
                                </div>

                                {/* Customer Name */}
                                <div className="col-1">
                                    <div className="table-header-label-wrapper">
                                        <span>{__('Customer Name')}</span>
                                    </div>
                                </div>

                                {/* Approved Phone */}
                                <div className="col-1">
                                    <div className="table-header-label-wrapper">
                                        <span>{__('Approved Phones')}</span>
                                    </div>
                                </div>

                                {/* Has Documents */}
                                <div className="col-1">
                                    <div className="table-header-label-wrapper">
                                        <span>{__('Attachments')}</span>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="col-1">
                                    <div className="table-header-label-wrapper">
                                        <span>{__('Delete')}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Table Body */}
                            <div className="table-body">
                                {carItems.length === 0 &&
                                    <div className="row ml-0 mr-0">
                                    <div className="col-12">
                                            <p>{__('No VINs found.')}</p>
                                        </div>
                                    </div>
                                }
                                {carItems.length > 0 && carItems.map((item, index) => {
                                    return (
                                        <Row
                                            key={index}
                                            item={item}
                                            deleteVin={this.deleteVin}
                                        />
                                    );
                                })}
                            </div>

                            {/* Table Footer */}
                            <div className="table-footer table-footer--dgb">
                                <div className="table-footer-label-wrapper">
                                    <span>
                                        <span className="mr-1">{__('Show rows per page')}</span>
                                        <select onChange={this.handleLimitChange} value={this.state.limit}>
                                            <option value="10">10</option>
                                            <option value="25">25</option>
                                            <option value="50">50</option>
                                        </select>
                                    </span>

                                    <div className="d-flex flex-row align-items-center">
                                        <span
                                            className="mr-3">{__(`${this.state.paginationFirstItemIndex} - ${(this.state.paginationFirstItemIndex + this.state.cars?.items.length - 1) || 0} of ${this.state.cars?.totalDocs}`)}</span>

                                        <button
                                            onClick={() => this.loadPrevPage()}
                                            disabled={!this.state.cars.prevPage}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">
                                                <path
                                                    d="M165.66 202.34a8 8 0 0 1-11.32 11.32l-80-80a8 8 0 0 1 0-11.32l80-80a8 8 0 0 1 11.32 11.32L91.31 128Z"/>
                                            </svg>
                                        </button>

                                        <button
                                            onClick={() => this.loadNextPage()}
                                            disabled={!this.state.cars.nextPage}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">
                                                <path
                                                    d="m181.66 133.66-80 80a8 8 0 0 1-11.32-11.32L164.69 128 90.34 53.66a8 8 0 0 1 11.32-11.32l80 80a8 8 0 0 1 0 11.32Z"/>
                                            </svg>
                                        </button>

                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Toast */}
                <Toaster
                    position="top-center"
                    toastOptions={{
                        duration: 2000,
                    }}
                />

            </div>
        )
    }
}

function mapStateToProps(state) {
    return {
        state: state,
        business: state.business,
    }
}

export default connect(mapStateToProps)(ManageVins)
